import { createPath, NavigationType, type Location, type Navigator, type To } from "react-router-dom";
import { z } from "zod";
import { getNavigationIntent, isFormStep, isSectionRoot, navigationPath, workspaceHome } from "./navigationPolicy";

const storageKey = "froppio:navigation:v1";
export const clearNavigationSession = () => window.sessionStorage.removeItem(storageKey);
const ledgerSchema = z.object({
  id: z.string(),
  identity: z.string().nullable(),
  entries: z.array(z.object({ key: z.string(), path: z.string() })).min(1),
});
const markerSchema = z.object({ id: z.string(), index: z.number().int().nonnegative() });
type Ledger = z.infer<typeof ledgerSchema>;
type Entry = { location: Location<unknown>; index: number };
export interface NavigationSnapshot {
  location: Location<unknown>;
  action: NavigationType;
  busy: boolean;
  canGoBack: boolean;
  error?: Error;
}

// Browser history owns traversal; the ledger identifies which entries still belong
// to the active flow. It stores URLs/keys only, never form data or credentials.
export function createAppHistory(browser: Window) {
  const history = browser.history;
  const listeners = new Set<() => void>();
  let ledger: Ledger;
  let current: Entry;
  let snapshot: NavigationSnapshot;
  let pending: { index: number; commit: () => void } | undefined;
  let timeout: number | undefined;
  let repairOnListen = false;

  const path = () => `${browser.location.pathname}${browser.location.search}${browser.location.hash}`;
  const location = (url: string, state: unknown = null, key = crypto.randomUUID()): Location<unknown> => {
    const parsed = new URL(url, browser.location.href);
    if (parsed.origin !== browser.location.origin) throw new Error("App navigation must stay on the Froppio origin.");
    return { pathname: parsed.pathname, search: parsed.search, hash: parsed.hash, state, key };
  };
  const persist = () => browser.sessionStorage.setItem(storageKey, JSON.stringify(ledger));
  function publish(action = NavigationType.Pop, error?: Error) {
    snapshot = { location: current.location, action, busy: !!pending, canGoBack: current.index > 0, error };
    listeners.forEach(listener => listener());
  }
  function fail(cause: unknown) {
    browser.clearTimeout(timeout);
    pending = undefined;
    publish(NavigationType.Pop, cause instanceof Error ? cause : new Error("App navigation failed."));
  }
  function write(next: Location<unknown>, index: number, replace: boolean) {
    const state = { usr: next.state, key: next.key, idx: index, froppio: { id: ledger.id, index } };
    history[replace ? "replaceState" : "pushState"](state, "", createPath(next));
    // Public callback URLs can contain one-time OAuth codes; never persist them.
    const entryPath = workspaceHome(next.pathname) ? createPath(next) : next.pathname;
    ledger.entries = [...ledger.entries.slice(0, index), { key: next.key, path: entryPath }];
    persist();
    current = { location: next, index };
  }
  function initialize(url: string, state: unknown = null) {
    const target = location(url, state);
    const home = workspaceHome(target.pathname);
    ledger = { id: crypto.randomUUID(), identity: null, entries: [] };
    write(home ? location(home) : target, 0, true);
    if (home && createPath(target) !== home) write(target, 1, false);
    publish();
  }
  function rewind(index: number, commit: () => void) {
    if (index === current.index) {
      commit();
      return;
    }
    pending = { index, commit };
    publish();
    timeout = browser.setTimeout(() => fail(new Error("Navigation did not finish. Reload Froppio to continue.")), 4000);
    history.go(index - current.index);
  }
  function reset(target: Location<unknown>) {
    rewind(0, () => {
      const home = workspaceHome(target.pathname);
      write(home ? location(home) : target, 0, true);
      if (home && createPath(target) !== home) write(target, 1, false);
      publish(NavigationType.Replace);
    });
  }
  function finish(target: Location<unknown>) {
    const targetPath = createPath(target);
    let previous = -1;
    for (let i = current.index - 1; i >= 0; i--) {
      const entryPath = ledger.entries[i].path;
      if (entryPath === targetPath || (!target.search && !target.hash
        && new URL(entryPath, browser.location.origin).pathname === target.pathname)) {
        previous = i;
        break;
      }
    }
    let index = current.index;
    if (previous >= 0) index = previous;
    else {
      while (index > 1 && isFormStep(ledger.entries[index - 1].path)) index--;
    }
    rewind(index, () => {
      write(target, index, true);
      publish(NavigationType.Replace);
    });
  }
  function navigate(to: To, state: unknown, replace: boolean) {
    // A history traversal is asynchronous. Do not let double taps race its commit.
    if (pending || snapshot.error) return;
    try {
      const target = location(navigationPath(to), state);
      const intent = getNavigationIntent(state);
      const samePath = createPath(target) === createPath(current.location);
      if (samePath && !replace && !intent) return;
      const scopeChanged = workspaceHome(target.pathname) !== workspaceHome(current.location.pathname);
      const sectionChanged = target.pathname !== current.location.pathname && isSectionRoot(target.pathname);
      const homeReset = workspaceHome(target.pathname) === createPath(target) && !samePath;
      if (scopeChanged || homeReset || intent === "start" || (sectionChanged && intent !== "push" && intent !== "finish")) {
        reset(target);
      } else if (intent === "finish") {
        finish(target);
      } else {
        write(target, replace ? current.index : current.index + 1, replace);
        publish(replace ? NavigationType.Replace : NavigationType.Push);
      }
    } catch (cause) { fail(cause); }
  }
  function pop() {
    try {
      const marker = markerSchema.safeParse(history.state?.froppio);
      if (!marker.success) {
        initialize(path(), history.state?.usr);
        return;
      }
      if (marker.data.id !== ledger.id) {
        // A previous document/account must not resurrect a retired app stack.
        initialize(ledger.entries[0].path);
        return;
      }
      const index = marker.data.index;
      const valid = ledger.entries[index]?.key === history.state?.key;
      if (pending) {
        if (index !== pending.index) {
          history.go(pending.index - index);
          return;
        }
        const commit = pending.commit;
        pending = undefined;
        browser.clearTimeout(timeout);
        if (!valid) throw new Error("The navigation stack changed unexpectedly. Reload Froppio to continue.");
        current = { location: location(path(), history.state.usr, history.state.key), index };
        commit();
      } else if (!valid) {
        // Forward into a discarded branch is bounced without rendering its page.
        pending = { index: current.index, commit: () => publish() };
        timeout = browser.setTimeout(() => fail(new Error("Could not restore the current navigation stack.")), 4000);
        publish();
        history.go(current.index - index);
      } else {
        current = { location: location(path(), history.state.usr, history.state.key), index };
        publish();
      }
    } catch (cause) { fail(cause); }
  }
  function resume(event: PageTransitionEvent) {
    if (!event.persisted) return;
    try {
      const saved = browser.sessionStorage.getItem(storageKey);
      const latest = saved ? ledgerSchema.parse(JSON.parse(saved)) : undefined;
      if (!latest || latest.id !== ledger.id || latest.identity !== ledger.identity
        || latest.entries[current.index]?.key !== current.location.key) {
        // A bfcache-restored document can belong to a flow/account retired by
        // a newer document. Re-enter through auth and stack initialization.
        browser.location.reload();
        return;
      }
      ledger = latest;
      publish();
    } catch (cause) { fail(cause); }
  }

  const saved = browser.sessionStorage.getItem(storageKey);
  const restored = saved ? ledgerSchema.parse(JSON.parse(saved)) : undefined;
  const marker = markerSchema.safeParse(history.state?.froppio);
  if (restored && marker.success && marker.data.id === restored.id) {
    ledger = restored;
    const valid = restored.entries[marker.data.index]?.key === history.state?.key;
    current = {
      location: valid ? location(path(), history.state.usr, history.state.key) : location(restored.entries[0].path),
      index: marker.data.index,
    };
    repairOnListen = !valid;
    publish();
  } else if (restored && marker.success) {
    initialize(restored.entries[0].path);
  } else initialize(path(), history.state?.usr);

  const navigator: Navigator = {
    createHref: navigationPath,
    encodeLocation: to => {
      const { pathname, search, hash } = location(navigationPath(to));
      return { pathname, search, hash };
    },
    push: (to, state) => navigate(to, state, false),
    replace: (to, state) => navigate(to, state, true),
    go: delta => {
      if (pending || snapshot.error || !delta) return;
      const index = Math.max(0, Math.min(ledger.entries.length - 1, current.index + delta));
      if (index !== current.index) {
        try { rewind(index, () => publish()); } catch (cause) { fail(cause); }
      } else publish();
    },
  };
  return {
    navigator,
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    listen: () => {
      browser.addEventListener("popstate", pop);
      browser.addEventListener("pageshow", resume);
      if (repairOnListen) {
        repairOnListen = false;
        reset(location(ledger.entries[0].path));
      } else if (pending) {
        timeout = browser.setTimeout(() => fail(new Error("Navigation did not finish. Reload Froppio to continue.")), 4000);
      }
      return () => {
        browser.removeEventListener("popstate", pop);
        browser.removeEventListener("pageshow", resume);
        browser.clearTimeout(timeout);
      };
    },
    setIdentity: (identity: string | null) => {
      try {
        if (ledger.identity === identity) return;
        const previous = ledger.identity;
        ledger.identity = identity;
        persist();
        if (previous !== null) {
          // Logout/account changes cannot retain another user's routes or drafts.
          const target = location(identity ? workspaceHome(current.location.pathname) ?? "/" : "/login");
          if (pending) pending.commit = () => reset(target);
          else reset(target);
        }
      } catch (cause) { fail(cause); }
    },
  };
}

export type AppHistory = ReturnType<typeof createAppHistory>;
